import * as React from "react";
import { Bookmark, Heart, MessageCircle, MoreHorizontal, Send } from "lucide-react";
import { Avatar } from "@/components/ui";
import { getInitials } from "@/components/reviews/utils";
import { AspectRatio } from "@/services/bedrock/dtos/DesignPackage";
import { DesignRenderGroup } from "@/services/bedrock/dtos/DesignRenderGroup";
import { cn } from "@/lib/utils";
import { RenderPreview, slideCount } from "./RenderPreview";

export interface PostPreviewProps {
  group: DesignRenderGroup | undefined;
  aspectRatio: AspectRatio;
  isInFlight: boolean;
  isFailed: boolean;
  onRetry: () => void;
  authorName: string;
  authorPhotoUrl?: string | null;
  /** The tallest the images may be (any CSS length); the card narrows to keep their shape. */
  maxMediaHeight?: string;
  /** A review whose slide to show once its render is on screen. */
  slideFocus?: SlideFocus | null;
  onSlideFocused?: () => void;
}

export interface SlideFocus {
  reviewId: number;
  /** The group on screen when the focus was asked for; its slide shows from the next group. Null shows it now. */
  afterGroupId: number | null;
}

const RATIO: Record<AspectRatio, number> = {
  square_1_1: 1,
  portrait_4_5: 4 / 5,
  portrait_9_16: 9 / 16,
};

/**
 * The player's post as it shows up in a feed: their name and photo, the images edge to edge, and the
 * action row. The caption isn't repeated here; it's edited in full right beside the preview.
 */
export const PostPreview: React.FC<PostPreviewProps> = ({
  group,
  aspectRatio,
  isInFlight,
  isFailed,
  onRetry,
  authorName,
  authorPhotoUrl,
  maxMediaHeight = "60vh",
  slideFocus,
  onSlideFocused,
}) => {
  const [index, setIndex] = React.useState(0);
  const total = slideCount(group);

  React.useEffect(() => {
    if (!slideFocus || !group || group.id === slideFocus.afterGroupId) return;
    const slides = [...group.designRenders].sort((a, b) => a.slideIndex - b.slideIndex);
    const at = slides.findIndex(render => Number(render.reviewId) === slideFocus.reviewId);
    // The new group's slides may not be listed yet; it updates as they're created.
    if (at < 0) return;
    setIndex(at);
    onSlideFocused?.();
  }, [slideFocus, group]);
  return (
    <div
      className="mx-auto w-full overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm"
      // The card hugs the images, so a tall 9:16 post has no blank sides.
      style={{ maxWidth: `min(100%, calc(${maxMediaHeight} * ${RATIO[aspectRatio]}))` }}
      data-testid="post-preview"
    >
      <div className="flex items-center gap-2.5 px-3 py-2.5">
        <Avatar className="size-8">
          {authorPhotoUrl && <Avatar.Image src={authorPhotoUrl} alt="" />}
          <Avatar.Fallback className="text-xs font-semibold">{getInitials(authorName)}</Avatar.Fallback>
        </Avatar>
        <div className="flex min-w-0 flex-col leading-tight">
          <span className="truncate text-[13px] font-semibold text-slate-900">{authorName}</span>
          <span className="text-[11px] text-slate-400">Just now</span>
        </div>
        <MoreHorizontal className="ml-auto size-4 text-slate-500" aria-hidden="true" />
      </div>
      {/* Hairlines above and below so a white image still shows where it ends. */}
      <div className="border-y border-slate-100">
        <RenderPreview
          group={group}
          aspectRatio={aspectRatio}
          isInFlight={isInFlight}
          isFailed={isFailed}
          onRetry={onRetry}
          imageClassName="rounded-none"
          maxHeight={null}
          pager="overlay"
          index={index}
          onIndexChange={setIndex}
        />
      </div>
      <div className="relative flex items-center gap-4 px-3 py-2.5 text-slate-800">
        <Heart className="size-5" strokeWidth={1.75} aria-hidden="true" />
        <MessageCircle className="size-5" strokeWidth={1.75} aria-hidden="true" />
        <Send className="size-5" strokeWidth={1.75} aria-hidden="true" />
        {/* Carousel dots sit in the action row, as a feed shows them, so they never cover the design. */}
        {total > 1 && (
          <div className="absolute left-1/2 flex -translate-x-1/2 gap-1" data-testid="carousel-dots" aria-hidden="true">
            {Array.from({ length: total }, (_, i) => (
              <span key={i} className={cn("size-1.5 rounded-full", i === index ? "bg-primary" : "bg-slate-300")} />
            ))}
          </div>
        )}
        <Bookmark className="ml-auto size-5" strokeWidth={1.75} aria-hidden="true" />
      </div>
    </div>
  );
};
